import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Columns3, Building2, Send, Settings as SettingsIcon, LogOut, MountainSnow, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/app/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/app/pipeline", label: "Pipeline", icon: Columns3, testid: "nav-pipeline" },
  { to: "/app/properties", label: "Properties", icon: Building2, testid: "nav-properties" },
  { to: "/launches", label: "New Launches", icon: Sparkles, testid: "nav-launches" },
  { to: "/app/campaigns", label: "Campaigns", icon: Send, testid: "nav-campaigns" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      {/* Sidebar */}
      <aside className="w-64 sidebar-noise text-[#F8F9FA] flex flex-col sticky top-0 h-screen">
        <div className="px-6 pt-7 pb-8 border-b border-white/5">
          <button
            data-testid="sidebar-brand"
            onClick={() => navigate("/app/dashboard")}
            className="flex items-center gap-3 text-left"
          >
            <div className="w-9 h-9 rounded-md bg-[#152243] border border-[#C5A880]/20 flex items-center justify-center">
              <MountainSnow className="w-5 h-5 text-[#C5A880]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-serif-display text-base leading-none">Alpine Straits</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#C5A880] mt-1">Property Partners</div>
            </div>
          </button>
          <a
            href="https://www.alpinestraitspartners.com.sg"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sidebar-website-link"
            className="mt-3 block text-[10px] tracking-[0.15em] uppercase text-slate-400 hover:text-[#C5A880] transition-colors"
          >
            alpinestraitspartners.com.sg ↗
          </a>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-[#152243] text-[#C5A880]"
                    : "text-slate-300 hover:bg-[#152243] hover:text-[#C5A880]"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="w-9 h-9 border border-[#C5A880]/30">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#152243] text-[#C5A880] text-xs">
                {user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            onClick={logout}
            className="w-full mt-2 text-slate-300 hover:bg-[#152243] hover:text-[#C5A880] justify-start"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
